import React, { useEffect, useState } from 'react';
import {
  View,
  Pressable,
  ScrollView,
  TextInput,
} from 'react-native';
import {
  heightPercentageToDP as hp,
} from 'react-native-responsive-screen';
import Constants from 'expo-constants';
import * as Auth from 'aws-amplify/auth';
import Toast from 'react-native-root-toast';

import { InlineLoader } from '../components/LoadingOverlay';
import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NotificationSubscriptionToggles from '../components/NotificationSubscriptionToggles';
import DeleteAccountModal from '../components/DeleteAccountModal';
import BadgeGrid from '../components/BadgeGrid';
import ToggleSwitch from '../components/ToggleSwitch';
import LanguageSelector from '../components/LanguageSelector';


const appVersion = Constants.expoConfig.version;
import { getOrganizationAccess, getQuietMode, setQuietMode as persistQuietMode } from '../util/storage';
import Util from '../util/util.js';
import styles from '../style/screens/settingsStyles';
import { useResponsiveStyles, useAchievements, useFeature } from '../hooks';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const Settings = (props) => {
  const { t } = useTranslation(['settings', 'common']);

  // Defining local state values
  const { organization, admin, userAdmin } = props.route.params;

  // Use responsive styles hook for platform detection
  const { isWeb, containerStyle } = useResponsiveStyles();

  // Staged rollout: the picker only appears for orgs with Spanish enabled.
  // Feature flags are cached at login, so flipping this needs a re-login.
  const languagePickerEnabled = useFeature('i18n-spanish');
  // Achievements/badges data
  const {
    loading: achievementsLoading,
    nextBadge,
    getBadgesWithStatus,
    streak,
    surpriseBadgeCount,
  } = useAchievements();
  const [email, setEmail] = useState(null);
  const [code, setCode] = useState('');
  const [reports, setReports] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [organizationAccess, setOrganizationAccess] = useState([organization]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [quietMode, setQuietMode] = useState(false);

  useEffect(() => {
    getUserReports();
    getUser();
    loadQuietMode();
  }, []);

  const loadQuietMode = async () => {
    setQuietMode(await getQuietMode());
  };

  const toggleQuietMode = async (value) => {
    setQuietMode(value);
    await persistQuietMode(value);
  };

  const getUser = async (_organization) => {
    const user = await Auth.fetchUserAttributes();
    setEmail(user.email);
  };

  const submitOrganizationCode = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    let params = {
      username: email,
      email: email,
      organization: organization,
      roles: ['Reporter'],
      code: code,
    };

    try {
      await Util.post('UserManagement', '/users/reset', {
        body: params,
      }).then(() => {
        setIsSubmitting(false);
        Toast.show(`✅ ${t('settings:userModified')}`, {
          backgroundColor: '#259e23',
          position: Toast.positions.BOTTOM,
          duration: 2500,
          textColor: '#fff',
        });
      });
      props.navigation.navigate('Login');
    } catch (error) {
      setIsSubmitting(false);
      // Keep error generic to prevent probing which orgs exist or what domains are allowed
      alert(`❌ ${t('settings:invalidOrgCode')}`);
    }
  };

  async function getUserReports() {
    // Get user's actual organization access for notification settings
    const orgAccess = await getOrganizationAccess();
    setOrganizationAccess(orgAccess.length ? [...new Set(orgAccess)] : [organization]);

    try {
      // Backend queries all user reports, excluding 'limited' sandbox (unless user is in it)
      const requestData = {
        queryParams: {
          organization: organization,
          countOnly: 'true',
        },
      };
      try {
        const data = await Util.get('Resty', '/items/listUserObjects/:user', requestData);
        // Use count from response (more efficient than fetching all items)
        const count = data.count !== undefined ? data.count : data.items.length;
        setReports(count.toString());
      } catch (error) {
        console.log(error);
        //setText(error.toString());
      }
    } catch (e) {
      console.log('failed2: ', e.message);
    }
  }

  const handleDeleteAccount = async (password) => {
    setIsDeleting(true);
    try {
      // Sign out first to allow fresh sign-in for password verification
      await Auth.signOut();

      // Verify password by signing in
      await Auth.signIn({ username: email, password });

      // Password verified, now delete account
      await Util.post('UserManagement', '/users/self-delete', {});

      // Account deleted - show toast and redirect
      // Don't try to sign out again (user no longer exists)
      Toast.show(t('settings:accountDeleted'), {
        backgroundColor: AppStyles.colorSet.success,
        position: Toast.positions.BOTTOM,
        duration: 3000,
        textColor: '#fff',
      });

      // Navigate to login
      props.navigation.reset({
        index: 0,
        routes: [{ name: 'Login', params: { skipAutoLogin: true } }],
      });
    } catch (error) {
      console.error('Delete account error:', error.name, error.message);
      setIsDeleting(false);
      // Check if it's an auth error (wrong password)
      if (error.name === 'NotAuthorizedException' || error.name === 'UserNotFoundException') {
        throw new Error(t('settings:incorrectPassword'));
      }
      throw new Error(error.response?.data?.error || error.message || t('settings:deleteFailed'));
    }
  };

  return (
    <View style={containerStyle}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} />

      <ScrollView
        style={styles.innerContainer}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 24 }}
      >
        {/* Page title */}
        <CustomText
          style={styles.title}
          font="semiBold"
          size={isWeb ? 28 : 24}
          color={AppStyles.colorSet.textPrimary}
        >
          {t('settings:title')}
        </CustomText>

        {/* Organization Code Section - only for limited users */}
        {organization === 'limited' && (
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
                {t('settings:joinOrganization')}
              </CustomText>
            </View>
            <View style={styles.fieldGroup}>
              <CustomText
                style={styles.fieldLabel}
                font="medium"
                size={14}
                color={AppStyles.colorSet.textSecondary}
              >
                {t('settings:organizationCode')}
              </CustomText>
              <TextInput
                value={code}
                onChangeText={(text) => setCode(text)}
                style={styles.textInput}
                placeholderTextColor={AppStyles.colorSet.textMuted}
                placeholder={t('settings:organizationCodePlaceholder')}
                allowFontScaling={false}
              />
            </View>
            <Pressable
              onPress={submitOrganizationCode}
              disabled={isSubmitting || !code.length}
            >
              {({ hovered, focused }) => (
              <View style={[
                styles.button,
                (isSubmitting || !code.length) && styles.buttonDisabled,
                !(isSubmitting || !code.length) && (hovered || focused) && styles.buttonHovered,
              ]}>
                <CustomText
                  font="medium"
                  size={16}
                  color={isSubmitting || !code.length ? AppStyles.colorSet.textMuted : '#000'}
                >
                  {t('settings:submitCode')} {isSubmitting && <InlineLoader />}
                </CustomText>
              </View>
              )}
            </Pressable>
          </View>
        )}

        {/* Profile Section */}
        <View style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
              {t('settings:profile')}
            </CustomText>
          </View>

          {/* Stats row - Reports count */}
          <View style={styles.statsRow}>
            <View style={styles.statCard}>
              {reports !== null ? (
                <>
                  <CustomText font="bold" size={28} color={AppStyles.colorSet.textPrimary}>
                    {reports}
                  </CustomText>
                  <CustomText font="regular" size={12} color={AppStyles.colorSet.textMuted}>
                    {t('settings:reportsCount', { count: Number(reports) })}
                  </CustomText>
                </>
              ) : (
                <InlineLoader />
              )}
            </View>
          </View>

          {/* Email - subtle display */}
          <View style={styles.emailRow}>
            {email ? (
              <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
                {email}
              </CustomText>
            ) : (
              <InlineLoader />
            )}
          </View>
        </View>

        {/* Recognition Section */}
        <View style={styles.recognitionSection}>
          <View style={styles.sectionHeader}>
            <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
              {t('settings:recognition')}
            </CustomText>
          </View>
          <BadgeGrid
            badges={getBadgesWithStatus()}
            nextBadge={nextBadge}
            streak={streak}
            loading={achievementsLoading}
            surpriseBadgeCount={surpriseBadgeCount}
          />
          {/* Quiet Mode Toggle */}
          <View style={styles.quietModeRow}>
            <View style={styles.quietModeText}>
              <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
                {t('settings:quietMode')}
              </CustomText>
              <CustomText font="regular" size={12} color={AppStyles.colorSet.textSecondary}>
                {t('settings:quietModeHint')}
              </CustomText>
            </View>
            <ToggleSwitch value={quietMode} onValueChange={toggleQuietMode} />
          </View>
        </View>

        {/* Account Settings Section - Change Password + Notifications */}
        <View style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
              {t('settings:accountSettings')}
            </CustomText>
          </View>

          <Pressable
            style={({ pressed }) => [{ cursor: 'pointer' }, pressed && { opacity: 0.8 }]}
            onPress={() => {
              props.navigation.navigate('ForgotPasswordInternal', {
                organization: organization,
                admin: admin,
                userAdmin: userAdmin
              });
            }}
          >
            {({ hovered, focused }) => (
              <View style={[styles.buttonSecondary, (hovered || focused) && styles.buttonSecondaryHovered]}>
                <CustomText font="medium" size={16} color={AppStyles.colorSet.textPrimary}>
                  {t('settings:changePassword')}
                </CustomText>
              </View>
            )}
          </Pressable>

          {/* Email Notification Preferences - only for admin users */}
          {admin && organization !== 'limited' && (
            <>
              <View style={[styles.separator, { marginVertical: 16 }]} />
              <CustomText
                style={styles.fieldLabel}
                font="medium"
                size={14}
                color={AppStyles.colorSet.textSecondary}
              >
                {t('settings:emailNotifications')}
              </CustomText>
              <NotificationSubscriptionToggles
                organizations={organizationAccess}
                isAdmin={false}
              />
            </>
          )}
        </View>

        {/* Language Section - gated per-org while translations are in progress */}
        {languagePickerEnabled && (
          <View style={styles.sectionCard}>
            <LanguageSelector />
          </View>
        )}

        {/* Danger Zone - Delete Account */}
        <View style={styles.dangerZone}>
          <View style={styles.dangerZoneHeader}>
            <CustomText font="semiBold" size={14} color={AppStyles.colorSet.error}>
              {t('settings:dangerZone')}
            </CustomText>
          </View>
          <CustomText
            font="regular"
            size={13}
            color={AppStyles.colorSet.textSecondary}
            style={{ marginBottom: 12 }}
          >
            {t('settings:dangerZoneHint')}
          </CustomText>
          <Pressable
            style={({ pressed }) => [{ cursor: 'pointer' }, pressed && { opacity: 0.6 }]}
            onPress={() => setShowDeleteModal(true)}
          >
            {({ hovered, focused }) => (
              <CustomText
                font="medium"
                size={14}
                color={AppStyles.colorSet.error}
                style={[styles.deleteAccountLink, (hovered || focused) && { opacity: 0.75 }]}
              >
                {t('settings:deleteMyAccount')}
              </CustomText>
            )}
          </Pressable>
        </View>

        {/* Version info */}
        <View style={styles.versionContainer}>
          <CustomText font="regular" size={13} color={AppStyles.colorSet.textMuted}>
            {t('settings:version', { version: appVersion })}
          </CustomText>
        </View>
      </ScrollView>

      <DeleteAccountModal
        visible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={isDeleting}
      />

      <Footer admin={admin} organization={organization} userAdmin={userAdmin} />
    </View>
  );
};

export default Settings;
